// src/services/byodLookup.ts
// --------------------------
// Client caller for the internal `byod-lookup` Edge Function (Stage 2/3). The EF
// does the grounded Gemini call (lookup) and the service-role band write (save);
// this is the thin invoke wrapper the responder Devices card + shelter wizard use.
// Nothing is stored by a lookup — the responder reviews, may edit, then saves.

import type {
  ByodSuggestion,
  ByodWriteFields,
} from "../../supabase/functions/_shared/byod/parseByodResult";
import { logger } from "../utils/logger";
import { getSupabaseClient } from "../utils/supabase";

// supabase-js reports a non-2xx from a function as the generic
// "Edge Function returned a non-2xx status code" and stashes the real HTTP
// Response on error.context. Our EF returns { error, detail } in the body — pull
// that out so callers see the ACTUAL reason (e.g. "Vertex lookup failed") instead.
async function efErrorText(error: {
  message?: string;
  context?: { json?: () => Promise<unknown> };
}): Promise<string> {
  const fallback = error?.message || "Edge Function error";
  try {
    const body = (await error?.context?.json?.()) as
      | { error?: string; detail?: unknown }
      | undefined;
    if (body?.error) {
      const detail =
        body.detail == null
          ? ""
          : `: ${typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail)}`;
      return `${body.error}${detail}`;
    }
  } catch {
    // body wasn't JSON / already consumed — fall through to the generic message
  }
  return fallback;
}

export interface ByodLookupArgs {
  schoolName: string;
  suburb?: string;
  state?: string;
  yearLevel?: string;
}

export interface ByodLookupResponse {
  ok: boolean;
  error?: string;
  suggestion?: ByodSuggestion;
  confidence?: "high" | "medium" | "low";
  sources?: string[];
  parsed?: boolean;
  finishReason?: string;
  rawPreview?: string;
}

// Run a grounded BYOD lookup for a school + year level. Returns the reviewable
// suggestion (one band). Never throws — network/EF errors come back as {ok:false}.
export async function lookupByod(args: ByodLookupArgs): Promise<ByodLookupResponse> {
  const c = getSupabaseClient();
  if (!c) return { ok: false, error: "No client" };
  try {
    const { data, error } = await c.functions.invoke("byod-lookup", {
      body: { action: "lookup", ...args },
    });
    if (error) return { ok: false, error: await efErrorText(error) };
    return (data ?? { ok: false, error: "Empty response" }) as ByodLookupResponse;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    logger.warn("lookupByod error:", message);
    return { ok: false, error: message };
  }
}

export interface SaveByodBandArgs {
  schoolId: number;
  // The responder-confirmed policy attributes (school_byod POLICY columns or the
  // short suggestion keys — the EF normalises either shape).
  fields: Partial<ByodWriteFields> | Record<string, unknown>;
  yearFrom: number;
  yearTo: number;
  source?: "ai" | "responder" | "import";
  policyYear?: number;
}

// Save a responder-confirmed band (deactivate-and-insert, one band per lookup).
// Returns {ok:true} or {ok:false,error}. Never throws.
export async function saveByodBand(
  args: SaveByodBandArgs
): Promise<{ ok: boolean; error?: string }> {
  const c = getSupabaseClient();
  if (!c) return { ok: false, error: "No client" };
  try {
    const { data, error } = await c.functions.invoke("byod-lookup", {
      body: { action: "save", ...args },
    });
    if (error) return { ok: false, error: await efErrorText(error) };
    const d = (data ?? {}) as { ok?: boolean; error?: string };
    return { ok: d.ok === true, error: d.error };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    logger.warn("saveByodBand error:", message);
    return { ok: false, error: message };
  }
}

// ── Whole-school (multi-band) — the admin "Edit School" manager (BYOD Part 7 / B2) ──

export interface ByodBandsLookupResponse {
  ok: boolean;
  error?: string;
  suggestions?: ByodSuggestion[];
  confidence?: "high" | "medium" | "low";
  sources?: string[];
  parsed?: boolean;
  finishReason?: string;
  rawPreview?: string;
}

// Run a grounded WHOLE-SCHOOL lookup (every band Prep–12). Returns the reviewable set
// of band suggestions. Never throws — network/EF errors come back as {ok:false}.
export async function lookupByodBands(args: {
  schoolName: string;
  suburb?: string;
  state?: string;
  // BYOD Part 10 (10a): optional phase scope (Primary 0-6 / Secondary 7-12); omit for whole-school.
  yearFrom?: number;
  yearTo?: number;
}): Promise<ByodBandsLookupResponse> {
  const c = getSupabaseClient();
  if (!c) return { ok: false, error: "No client" };
  try {
    const { data, error } = await c.functions.invoke("byod-lookup", {
      body: { action: "lookup_bands", ...args },
    });
    if (error) return { ok: false, error: await efErrorText(error) };
    return (data ?? { ok: false, error: "Empty response" }) as ByodBandsLookupResponse;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    logger.warn("lookupByodBands error:", message);
    return { ok: false, error: message };
  }
}

export interface SaveByodBandsArgs {
  schoolId: number;
  // The admin-confirmed SET of bands; overlaps are reconciled across the whole set by the EF.
  bands: Array<{
    fields: Partial<ByodWriteFields> | Record<string, unknown>;
    yearFrom: number;
    yearTo: number;
  }>;
  source?: "ai" | "responder" | "import";
  policyYear?: number;
}

// Save the admin-confirmed set of bands at once (deactivate-overlapping-then-insert-all).
// Returns {ok:true, saved:n} or {ok:false,error}. Never throws.
export async function saveByodBands(
  args: SaveByodBandsArgs
): Promise<{ ok: boolean; error?: string; saved?: number }> {
  const c = getSupabaseClient();
  if (!c) return { ok: false, error: "No client" };
  try {
    const { data, error } = await c.functions.invoke("byod-lookup", {
      body: { action: "save_bands", ...args },
    });
    if (error) return { ok: false, error: await efErrorText(error) };
    const d = (data ?? {}) as { ok?: boolean; error?: string; saved?: number };
    return { ok: d.ok === true, error: d.error, saved: d.saved };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    logger.warn("saveByodBands error:", message);
    return { ok: false, error: message };
  }
}
